import type {
	BoardAgentAgentAvailability,
	BoardAgentAvailableAgentsResult,
	BoardAgentModelAvailability,
} from "@src/components/board/hooks/board-agent-available-models";
import type { SupportedAgent, SupportedModel } from "@src/gql/graphql";
import { type ReactNode, createContext, useContext } from "react";

export type {
	BoardAgentAgentAvailability,
	BoardAgentAvailableAgentsResult,
	BoardAgentModelAvailability,
};

export type BoardAgentViewport = {
	x: number;
	y: number;
	w: number;
	h: number;
};

export type BoardAgentCard = {
	shapeId: string;
	cardId: string;
	type: string;
	x: number;
	y: number;
	w: number;
	h: number;
};

export type BoardAgentCardSummary = {
	cardId: string;
	shapeId: string;
	type: string;
	typeLabel: string;
	isSelected: boolean;
	summary: string;
	contentLength: number;
	isEmpty: boolean;
};

export type BoardAgentVisibleCardsResult = {
	whiteboardId: string;
	whiteboardName: string;
	editable: boolean;
	cardCount: number;
	selectedCount: number;
	cards: BoardAgentCardSummary[];
};

export type BoardAgentSelectedCardsResult = {
	whiteboardId: string;
	whiteboardName: string;
	cardCount: number;
	cards: BoardAgentCardSummary[];
};

export type BoardAgentCardContentResult = {
	cardId: string;
	found: boolean;
	type?: string;
	typeLabel?: string;
	content?: string;
	isEmpty?: boolean;
};

export type BoardAgentShapeSummary = {
	shapeId: string;
	cardId?: string;
	type: string;
	typeLabel: string;
	isSelected: boolean;
	x: number;
	y: number;
	w: number;
	h: number;
	summary: string;
};

export type BoardAgentCreateShapeResult = {
	shapeId: string;
	summary?: string;
	bounds?: {
		x: number;
		y: number;
		w: number;
		h: number;
	};
	type?: string;
};

export type BoardAgentArrowConnection = {
	arrowId: string;
	label: string;
	fromShapeId?: string;
	toShapeId?: string;
	connected: boolean;
};

export type BoardAgentDiagramOverlap = {
	shapeIdA: string;
	shapeIdB: string;
	summaryA: string;
	summaryB: string;
};

export type BoardAgentDetachedDrawPart = {
	shapeId: string;
	summary: string;
	nearestGapPx: number | null;
	nearestNeighborId: string | null;
};

export type BoardAgentSparseDrawStroke = {
	shapeId: string;
	summary: string;
	pointCount: number;
	spanPx: number;
};

export type BoardAgentInspectDiagramResult = {
	whiteboardId: string;
	whiteboardName: string;
	shapeCount: number;
	shapes: BoardAgentShapeSummary[];
	arrows: BoardAgentArrowConnection[];
	arrowCount: number;
	unboundArrowCount: number;
	unboundArrows: BoardAgentArrowConnection[];
	overlaps: BoardAgentDiagramOverlap[];
	overlapCount: number;
	overlappingArrows: BoardAgentDiagramOverlap[];
	overlappingArrowCount: number;
	detachedDrawParts: BoardAgentDetachedDrawPart[];
	detachedDrawPartCount: number;
	sparseDrawStrokes: BoardAgentSparseDrawStroke[];
	sparseDrawStrokeCount: number;
};

export type BoardAgentShapesListResult = {
	whiteboardId: string;
	whiteboardName: string;
	shapeCount: number;
	shapes: BoardAgentShapeSummary[];
};

export type BoardAgentArrowConnectionsResult = {
	whiteboardId: string;
	whiteboardName: string;
	arrowCount: number;
	arrows: BoardAgentArrowConnection[];
};

export type BoardAgentShapeContentResult = {
	shapeId: string;
	found: boolean;
	type?: string;
	typeLabel?: string;
	cardId?: string;
	content?: string;
	isEmpty?: boolean;
	bounds?: {
		x: number;
		y: number;
		w: number;
		h: number;
	};
	propsSummary?: Record<string, unknown>;
};

export type BoardAgentCustomShapesResult = {
	whiteboardId: string;
	shapeCount: number;
	shapes: BoardAgentShapeSummary[];
};

export type BoardAgentCustomShapeInfo =
	| {
			shapeId: string;
			type: string;
			typeLabel: string;
			editable: boolean;
			bounds?: { x: number; y: number; w: number; h: number };
			kind: "card";
			cardId: string;
			cardType: string;
	  }
	| {
			shapeId: string;
			type: string;
			typeLabel: string;
			editable: boolean;
			bounds?: { x: number; y: number; w: number; h: number };
			kind: "card-section";
			name: string;
			layoutMode: string;
			maxCols: number;
			maxRows: number;
			boundCardCount: number;
	  }
	| {
			shapeId: string;
			type: string;
			typeLabel: string;
			editable: boolean;
			bounds?: { x: number; y: number; w: number; h: number };
			kind: "image";
			url?: string;
			originalW?: number;
			originalH?: number;
			error?: string;
	  }
	| {
			shapeId: string;
			type: string;
			typeLabel: string;
			editable: boolean;
			bounds?: { x: number; y: number; w: number; h: number };
			kind: "custom";
	  };

export type BoardAgentFindEmptySpaceResult = {
	found: boolean;
	x?: number;
	y?: number;
	reason?: string;
};

export type BoardAgentSnapshot = {
	whiteboardId: string;
	whiteboardName: string;
	selectedCardIds: string[];
	visibleCardIds: string[];
	selectedCards: BoardAgentCard[];
	visibleCards: BoardAgentCard[];
	viewport: BoardAgentViewport;
	editable: boolean;
	visibleShapeCountByType: Record<string, number>;
};

export type BoardAgentActions = {
	getSnapshot: () => Promise<BoardAgentSnapshot>;
	listVisibleCards: () => Promise<BoardAgentVisibleCardsResult>;
	listSelectedCards: () => Promise<BoardAgentSelectedCardsResult>;
	getCardContent: (cardId: string) => Promise<BoardAgentCardContentResult>;
	openCard: (cardId: string) => Promise<{
		cardId: string;
		found: boolean;
		opened: boolean;
	}>;
	navigateToCard: (cardId: string) => Promise<{
		cardId: string;
		found: boolean;
	}>;
	createNoteCard: (input: {
		title: string;
		content?: string;
	}) => Promise<{
		cardId: string;
		shapeId: string;
		title: string;
	}>;
	listPromptCards: () => Promise<{
		whiteboardId: string;
		promptCards: BoardAgentCard[];
	}>;
	listPreprintCards: () => Promise<{
		whiteboardId: string;
		preprintCards: BoardAgentCard[];
	}>;
	createPromptCard: (input: {
		prompt: string;
	}) => Promise<{
		cardId: string;
		shapeId: string;
		prompt: string;
	}>;
	generateFromPromptCard: (input: {
		cardId: string;
		promptText: string;
		model?: SupportedModel;
		agent?: SupportedAgent;
		webSearch?: boolean;
	}) => Promise<{
		cardId: string;
		found: boolean;
		started: boolean;
		preprintShapeId?: string;
		model: SupportedModel;
		agent: SupportedAgent;
	}>;
	generateFromPromptCardAllModels: (input: {
		cardId: string;
		promptText: string;
		agent?: SupportedAgent;
		webSearch?: boolean;
	}) => Promise<{
		cardId: string;
		found: boolean;
		started: boolean;
		agent: SupportedAgent;
		modelCount: number;
		models: SupportedModel[];
		preprintShapeIds: string[];
	}>;
	listAvailableAgentsAndModels: (input?: {
		agent?: SupportedAgent;
		webSearch?: boolean;
	}) => Promise<BoardAgentAvailableAgentsResult>;
	fitBoardToContent: () => Promise<{
		fitted: boolean;
	}>;
	openBoardSearch: () => Promise<{
		opened: boolean;
	}>;
	listShapesInViewport: () => Promise<BoardAgentShapesListResult>;
	listShapesOnPage: (input?: {
		type?: string;
	}) => Promise<BoardAgentShapesListResult>;
	listSelectedShapes: () => Promise<BoardAgentShapesListResult>;
	getArrowConnections: () => Promise<BoardAgentArrowConnectionsResult>;
	inspectDiagram: () => Promise<BoardAgentInspectDiagramResult>;
	getShapeContent: (shapeId: string) => Promise<BoardAgentShapeContentResult>;
	navigateToShape: (shapeId: string) => Promise<{
		shapeId: string;
		found: boolean;
	}>;
	listCustomShapes: () => Promise<BoardAgentCustomShapesResult>;
	getCustomShapeInfo: (
		shapeId: string,
	) => Promise<BoardAgentCustomShapeInfo & { found: boolean }>;
	findEmptySpace: (input: {
		width: number;
		height: number;
		nearViewport?: boolean;
		padding?: number;
	}) => Promise<BoardAgentFindEmptySpaceResult>;
	createGeoShape: (input: {
		geo: string;
		x: number;
		y: number;
		w: number;
		h: number;
		text?: string;
		color?: string;
		fill?: string;
		dash?: string;
		size?: string;
		font?: string;
		align?: string;
		verticalAlign?: string;
		labelColor?: string;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			type: string;
			geo?: string;
			hint?: string;
		}
	>;
	createTextShape: (input: {
		x: number;
		y: number;
		text: string;
	}) => Promise<BoardAgentCreateShapeResult & { type: string }>;
	createArrow: (input: {
		fromShapeId?: string;
		toShapeId?: string;
		x1?: number;
		y1?: number;
		x2?: number;
		y2?: number;
		label?: string;
		kind?: string;
		bend?: number;
		elbowMidPoint?: number;
	}) => Promise<
		| (BoardAgentCreateShapeResult & {
				success: true;
				type: string;
				connected: boolean;
		  })
		| {
				success: false;
				error: string;
				hint: string;
				invalidShapeIds?: string[];
		  }
	>;
	createDrawShape: (input: {
		points: Array<{ x: number; y: number }>;
		color?: string;
		size?: string;
		dash?: string;
		isClosed?: boolean;
		fill?: string;
	}) => Promise<BoardAgentCreateShapeResult & { type: string }>;
	createHighlightShape: (input: {
		points: Array<{ x: number; y: number }>;
		color?: string;
		size?: string;
	}) => Promise<BoardAgentCreateShapeResult & { type: string }>;
	createLineShape: (input: {
		points: Array<{ x: number; y: number }>;
		color?: string;
		size?: string;
		spline?: string;
	}) => Promise<BoardAgentCreateShapeResult & { type: string }>;
	createNoteShape: (input: {
		x: number;
		y: number;
		text: string;
		color?: string;
	}) => Promise<BoardAgentCreateShapeResult & { type: string }>;
	createFrameShape: (input: {
		x: number;
		y: number;
		w: number;
		h: number;
		name?: string;
		color?: string;
	}) => Promise<BoardAgentCreateShapeResult & { type: string }>;
	updateGeoShape: (input: {
		shapeId: string;
		x?: number;
		y?: number;
		w?: number;
		h?: number;
		text?: string;
		color?: string;
		geo?: string;
		fill?: string;
		dash?: string;
		size?: string;
		font?: string;
		align?: string;
		verticalAlign?: string;
		labelColor?: string;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			type: string;
			updated?: boolean;
		}
	>;
	updateArrow: (input: {
		shapeId: string;
		label?: string;
		fromShapeId?: string;
		toShapeId?: string;
		kind?: string;
		bend?: number;
		elbowMidPoint?: number;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			type: string;
			updated?: boolean;
			rebound?: boolean;
			error?: string;
		}
	>;
	updateTextShape: (input: {
		shapeId: string;
		x?: number;
		y?: number;
		text?: string;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			type: string;
			updated?: boolean;
		}
	>;
	updateNoteShape: (input: {
		shapeId: string;
		x?: number;
		y?: number;
		text?: string;
		color?: string;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			type: string;
			updated?: boolean;
		}
	>;
	updateFrameShape: (input: {
		shapeId: string;
		x?: number;
		y?: number;
		w?: number;
		h?: number;
		name?: string;
		color?: string;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			type: string;
			updated?: boolean;
		}
	>;
	updateDrawShape: (input: {
		shapeId: string;
		x?: number;
		y?: number;
		points?: Array<{ x: number; y: number }>;
		color?: string;
		size?: string;
		dash?: string;
		isClosed?: boolean;
		fill?: string;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			type: string;
			updated?: boolean;
		}
	>;
	updateLineShape: (input: {
		shapeId: string;
		x?: number;
		y?: number;
		points?: Array<{ x: number; y: number }>;
		color?: string;
		size?: string;
		spline?: string;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			type: string;
			updated?: boolean;
		}
	>;
	updateHighlightShape: (input: {
		shapeId: string;
		x?: number;
		y?: number;
		points?: Array<{ x: number; y: number }>;
		color?: string;
		size?: string;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			type: string;
			updated?: boolean;
		}
	>;
	updateImageShape: (input: {
		shapeId: string;
		x?: number;
		y?: number;
		w?: number;
		h?: number;
		url?: string;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			type: string;
			updated?: boolean;
		}
	>;
	moveShape: (input: {
		shapeId: string;
		x: number;
		y: number;
	}) => Promise<
		BoardAgentCreateShapeResult & {
			found: boolean;
			moved?: boolean;
		}
	>;
	resizeShapes: (input: {
		shapeIds: string[];
		scaleX?: number;
		scaleY?: number;
		dw?: number;
		dh?: number;
	}) => Promise<{
		resized: boolean;
		shapeCount?: number;
		reason?: string;
	}>;
	rotateShapes: (input: {
		shapeIds: string[];
		degrees?: number;
		radians?: number;
	}) => Promise<{
		rotated: boolean;
		shapeCount?: number;
		reason?: string;
	}>;
	flipShapes: (input: {
		shapeIds: string[];
		axis: "horizontal" | "vertical";
	}) => Promise<{
		flipped: boolean;
		axis?: string;
		shapeCount?: number;
		reason?: string;
	}>;
	nudgeShapes: (input: {
		shapeIds: string[];
		dx: number;
		dy: number;
	}) => Promise<{
		nudged: boolean;
		shapeCount?: number;
		reason?: string;
	}>;
	lockShapes: (input: { shapeIds: string[] }) => Promise<{
		updated: boolean;
		locked?: boolean;
		shapeCount?: number;
		reason?: string;
	}>;
	unlockShapes: (input: { shapeIds: string[] }) => Promise<{
		updated: boolean;
		locked?: boolean;
		shapeCount?: number;
		reason?: string;
	}>;
	setCamera: (input: { x: number; y: number; z: number }) => Promise<{
		updated: boolean;
		x: number;
		y: number;
		z: number;
	}>;
	getCamera: () => Promise<{
		x: number;
		y: number;
		z: number;
	}>;
	selectShapes: (input: { shapeIds: string[] }) => Promise<{
		selected: boolean;
		shapeCount?: number;
	}>;
	moveCard: (input: {
		cardId: string;
		x: number;
		y: number;
	}) => Promise<{
		cardId: string;
		shapeId?: string;
		found: boolean;
		moved?: boolean;
	}>;
	resizeCard: (input: {
		cardId: string;
		w?: number;
		h?: number;
	}) => Promise<{
		cardId: string;
		shapeId?: string;
		found: boolean;
		resized?: boolean;
		reason?: string;
	}>;
	updateCardContent: (input: {
		cardId: string;
		title?: string;
		content?: string;
	}) => Promise<{
		cardId: string;
		updated: boolean;
		reason?: string;
	}>;
	deleteCard: (input: { cardId: string }) => Promise<{
		cardId: string;
		shapeId?: string;
		found: boolean;
		deleted: boolean;
	}>;
	duplicateCard: (input: {
		cardId: string;
		offsetX?: number;
		offsetY?: number;
	}) => Promise<{
		cardId: string;
		newCardId?: string;
		shapeId?: string;
		found: boolean;
		duplicated: boolean;
	}>;
	lockCard: (input: { cardId: string }) => Promise<{
		cardId: string;
		shapeId?: string;
		found: boolean;
		updated: boolean;
		locked?: boolean;
	}>;
	unlockCard: (input: { cardId: string }) => Promise<{
		cardId: string;
		shapeId?: string;
		found: boolean;
		updated: boolean;
		locked?: boolean;
	}>;
	createCardSection: (input: {
		cardIds?: string[];
		shapeIds?: string[];
		name?: string;
		x?: number;
		y?: number;
		w?: number;
		h?: number;
	}) => Promise<{
		created: boolean;
		sectionId?: string;
		shapeCount?: number;
		reason?: string;
	}>;
	moveShapesToCardSection: (input: {
		sectionId: string;
		cardIds?: string[];
		shapeIds?: string[];
	}) => Promise<{
		reparented: boolean;
		sectionId?: string;
		shapeCount?: number;
		reason?: string;
	}>;
	runBoardPlan: (input: {
		reason?: string;
		steps: Array<{ op: string; input: Record<string, unknown> }>;
		stopOnError?: boolean;
	}) => Promise<{
		applied: number;
		failedAt?: number;
		steps: Array<{
			op: string;
			ok: boolean;
			result?: unknown;
			error?: string;
			resolvedInput?: Record<string, unknown>;
		}>;
		touchedShapeIds: string[];
		reason?: string;
	}>;
	alignShapes: (input: {
		shapeIds: string[];
		operation:
			| "left"
			| "center-horizontal"
			| "right"
			| "top"
			| "center-vertical"
			| "bottom";
	}) => Promise<{
		aligned: boolean;
		shapeCount?: number;
		operation?: string;
		reason?: string;
	}>;
	distributeShapes: (input: {
		shapeIds: string[];
		operation: "horizontal" | "vertical";
	}) => Promise<{
		distributed: boolean;
		shapeCount?: number;
		operation?: string;
		reason?: string;
	}>;
	duplicateShapes: (input: {
		shapeIds: string[];
		offsetX?: number;
		offsetY?: number;
	}) => Promise<{
		duplicated: boolean;
		shapeIds: string[];
		shapeCount?: number;
		reason?: string;
	}>;
	groupShapes: (input: { shapeIds: string[] }) => Promise<{
		grouped: boolean;
		groupId?: string;
		shapeCount?: number;
		reason?: string;
	}>;
	ungroupShapes: (input: { shapeIds: string[] }) => Promise<{
		ungrouped: boolean;
		shapeCount?: number;
		reason?: string;
	}>;
	moveShapesToFrame: (input: {
		shapeIds: string[];
		frameId: string;
	}) => Promise<{
		reparented: boolean;
		frameId?: string;
		shapeCount?: number;
		reason?: string;
	}>;
	setShapeZOrder: (input: {
		shapeIds: string[];
		operation: "front" | "back";
	}) => Promise<{
		updated: boolean;
		operation?: string;
		shapeCount?: number;
		reason?: string;
	}>;
	exportViewportImage: (input?: {
		shapeIds?: string[];
	}) => Promise<
		| {
				exported: true;
				format: "png";
				width: number;
				height: number;
				byteSize: number;
				base64: string;
				shapeCount: number;
				scope: "explicit" | "session" | "selection";
				scale: number;
		  }
		| {
				exported: false;
				reason: string;
				width?: number;
				height?: number;
		  }
	>;
	getBoardDocumentSnapshot: () => Promise<{
		whiteboardId: string;
		whiteboardName: string;
		snapshot: unknown;
		byteSize: number;
		shapeCount: number;
	}>;
	createImageShape: (input: {
		url: string;
		x: number;
		y: number;
		w: number;
		h: number;
	}) => Promise<BoardAgentCreateShapeResult & { type: string; url: string }>;
	deleteShape: (shapeId: string) => Promise<{
		shapeId: string;
		found: boolean;
		deleted: boolean;
	}>;
};

export type BoardAgentContextValue = {
	snapshot: BoardAgentSnapshot | null;
	actions: BoardAgentActions | null;
	refreshSnapshot?: () => void;
	focusSessionTouchedShapes?: (shapeIds?: string[]) => {
		focused: boolean;
		shapeCount?: number;
		shapeIds?: string[];
		reason?: string;
	};
	focusShape?: (shapeIdOrCardId: string) => boolean;
	focusBoard?: () => void;
};

const BoardAgentContext = createContext<BoardAgentContextValue | null>(null);

BoardAgentContext.displayName = "BoardAgentContext";

export function BoardAgentProvider({
	children,
	value,
}: {
	children: ReactNode;
	value: BoardAgentContextValue;
}) {
	return (
		<BoardAgentContext.Provider value={value}>
			{children}
		</BoardAgentContext.Provider>
	);
}

export function useBoardAgentContext() {
	return useContext(BoardAgentContext);
}
